import React, { ReactElement } from "react";
import { Button, ButtonGroup, ModalBox, Text } from "@jarvis-catalyst/ui-components";
import LoadingComponent from "../Loading";
import { useDeviceById } from "../../hooks/useDeviceById";
import DataTable from "react-data-table-component";
import { baseColumnsDeviceDetailTable } from "../../utils/deviceDetailTable";
import { paginationComponentOptions, paginationRowsPerPageOptions } from "../../config/constants";

type Props = {
  size?: "md" | "lg";
  title: string;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  deviceId: string;
  serviceProvider: string;
  username: string;
  ciamId: string;
};

const DeviceDetailModal = ({
  isOpen,
  setIsOpen,
  title,
  size = "md",
  deviceId,
  serviceProvider,
  username,
  ciamId
}: Props): ReactElement => {
  const { devicesByIdQuery, setFetched } = useDeviceById({ serviceProvider, ciamId, username, deviceId });

  React.useEffect(() => {
    setFetched(true);
    devicesByIdQuery.refetch();
  }, []);

  return (
    <div>
      <>
        <ModalBox
          size={size}
          isOpen={isOpen}
          onBackgroundClose={() => setIsOpen(false)}
          children={
            <>
              {devicesByIdQuery.isLoading ? (
                <LoadingComponent />
              ) : (
                <>
                  <div>
                    {devicesByIdQuery.data?.registeredDeviceData?.browserDetails && (
                      <div className="row">
                        <h5>Browser Details</h5>
                        <div className="col-12 col-md-6 col-lg-4">
                          <Text className="form-label">Browser Name</Text>
                          <input
                            type="text"
                            className="form-control"
                            disabled
                            value={
                              devicesByIdQuery.data?.registeredDeviceData?.browserDetails?.browserName ?? ""
                            }
                          />
                        </div>

                        <div className="col-12 col-md-6 col-lg-4">
                          <Text className="form-label">Browser Version</Text>
                          <input
                            type="text"
                            className="form-control"
                            disabled
                            value={
                              devicesByIdQuery.data?.registeredDeviceData?.browserDetails
                                ?.browserFullVersion ?? ""
                            }
                          />
                        </div>

                        <div className="col-12 col-md-6 col-lg-4">
                          <Text className="form-label">Operation System</Text>
                          <input
                            type="text"
                            className="form-control"
                            disabled
                            value={devicesByIdQuery.data?.registeredDeviceData?.browserDetails.os ?? ""}
                          />
                        </div>
                        <div className="col-12 col-md-6 col-lg-4">
                          <Text className="form-label">Operation Version</Text>
                          <input
                            type="text"
                            className="form-control"
                            disabled
                            value={
                              devicesByIdQuery.data?.registeredDeviceData?.browserDetails.osVersion ?? ""
                            }
                          />
                        </div>
                        <div className="col-12 col-md-6 col-lg-4">
                          <Text className="form-label">Incognito</Text>
                          <input
                            type="text"
                            className="form-control"
                            disabled
                            value={
                              devicesByIdQuery.data?.registeredDeviceData?.browserDetails.incognito === true
                                ? "Yes"
                                : "No"
                            }
                          />
                        </div>

                        <div className="col-12 col-md-6 col-lg-4">
                          <Text className="form-label">Context ID</Text>
                          <input
                            type="text"
                            className="form-control"
                            disabled
                            value={
                              devicesByIdQuery.data?.registeredDeviceData?.browserDetails?.contextId ?? ""
                            }
                          />
                        </div>
                        <div className="col-12">
                          <Text className="form-label">User Agent</Text>
                          <textarea
                            className="form-control"
                            disabled
                            value={
                              devicesByIdQuery.data?.registeredDeviceData?.browserDetails?.userAgent ?? ""
                            }
                          />
                        </div>
                      </div>
                    )}
                    {devicesByIdQuery.data?.registeredDeviceData?.appDeviceDetails && (
                      <div className="row">
                        <h5>App Details</h5>
                        <div className="col-12 col-md-6 col-lg-4">
                          <Text className="form-label">App Name</Text>
                          <input
                            type="text"
                            className="form-control"
                            disabled
                            value={
                              devicesByIdQuery.data?.registeredDeviceData?.appDeviceDetails?.appName ?? ""
                            }
                          />
                        </div>

                        <div className="col-12 col-md-6 col-lg-4">
                          <Text className="form-label">App Version</Text>
                          <input
                            type="text"
                            className="form-control"
                            disabled
                            value={
                              devicesByIdQuery.data?.registeredDeviceData?.appDeviceDetails?.appVersion ?? ""
                            }
                          />
                        </div>

                        <div className="col-12 col-md-6 col-lg-4">
                          <Text className="form-label">Operation System</Text>
                          <input
                            type="text"
                            className="form-control"
                            disabled
                            value={devicesByIdQuery.data?.registeredDeviceData?.appDeviceDetails.os ?? ""}
                          />
                        </div>
                        <div className="col-12 col-md-6 col-lg-4">
                          <Text className="form-label">Operation Version</Text>
                          <input
                            type="text"
                            className="form-control"
                            disabled
                            value={
                              devicesByIdQuery.data?.registeredDeviceData?.appDeviceDetails.osVersion ?? ""
                            }
                          />
                        </div>
                        <div className="col-12 col-md-6 col-lg-4">
                          <Text className="form-label">Unique Device Id</Text>
                          <input
                            type="text"
                            className="form-control"
                            disabled
                            value={
                              devicesByIdQuery.data?.registeredDeviceData?.appDeviceDetails?.uniqueDeviceId ??
                              ""
                            }
                          />
                        </div>
                      </div>
                    )}
                    <hr />
                    <div className="row">
                      <div className="col-12">
                        <DataTable
                          title="Device Sessions"
                          columns={baseColumnsDeviceDetailTable}
                          data={devicesByIdQuery.data?.customerSessionData || []}
                          pagination
                          paginationPerPage={20}
                          fixedHeaderScrollHeight="600px"
                          paginationRowsPerPageOptions={paginationRowsPerPageOptions}
                          fixedHeader
                          paginationComponentOptions={paginationComponentOptions}
                          noDataComponent="No sessions found"
                          customStyles={{
                            rows: {
                              style: {
                                minHeight: "45px"
                              }
                            },
                            headCells: {
                              style: {
                                paddingLeft: "8px",
                                paddingRight: "8px"
                              }
                            },
                            cells: {
                              style: {
                                paddingLeft: "8px",
                                paddingRight: "8px"
                              }
                            }
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}
            </>
          }
          components={{
            Header: () => (
              <>
                <h3>{title}</h3>
              </>
            ),
            Footer: () => (
              <>
                <ButtonGroup>
                  <Button onClick={() => setIsOpen(false)}>Cerrar</Button>
                </ButtonGroup>
              </>
            )
          }}
        />
      </>
    </div>
  );
};

export default DeviceDetailModal;
